'use client';

import type { PostCardProps } from '@routeroyal/theme-core';
import Link from 'next/link';
import Image from 'next/image';

/**
 * Route Royal Post Card
 * 
 * Clean, professional blog card with:
 * - Blue accent colors
 * - Inter font family
 * - Modern shadows and transitions
 */
export function RouteRoyalPostCard({
  post,
  variant = 'default',
  showExcerpt = true,
  showCategory = true,
  showAuthor = true,
  showDate = true,
  showReadingTime = true,
  showTags = false,
  locale = 'en',
  className = '',
  basePath = '/blog',
  messages,
}: PostCardProps) {
  const isCompact = variant === 'compact';
  const isFeatured = variant === 'featured';
  const isHorizontal = variant === 'horizontal';

  const postUrl = `/${locale}${basePath}/${post.slug}`;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatReadingTime = (minutes?: number) => {
    if (!minutes) return '';
    const label = messages?.minuteRead || 'min read';
    return `${minutes} ${label}`;
  };

  // Horizontal variant
  if (isHorizontal) {
    return (
      <article
        className={`
          group relative bg-white rounded-lg overflow-hidden
          border border-gray-200 hover:border-blue-200
          hover:shadow-lg transition-all duration-300
          flex flex-col sm:flex-row
          ${className}
        `}
      >
        <Link href={postUrl} className="flex flex-col sm:flex-row w-full">
          {post.featuredImage && (
            <div className="relative w-full sm:w-56 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
              <Image
                src={post.featuredImage.url}
                alt={post.featuredImage.alt || post.title}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-300"
                sizes="(max-width: 640px) 100vw, 224px"
              />
            </div>
          )}
          <div className="flex-1 p-5 flex flex-col justify-center">
            {showCategory && post.category && (
              <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
                {post.category.name}
              </span>
            )}
            <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
              {post.title}
            </h3>
            {showExcerpt && post.excerpt && (
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{post.excerpt}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-auto">
              {showAuthor && <span className="font-medium">{post.author.name}</span>}
              {showAuthor && showDate && <span>·</span>}
              {showDate && post.publishedAt && (
                <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
              )}
              {showReadingTime && post.readingTime && (
                <>
                  <span>·</span>
                  <span>{formatReadingTime(post.readingTime)}</span>
                </>
              )}
            </div>
          </div>
        </Link>
      </article>
    );
  }

  // Default/Compact/Featured variant
  return (
    <article
      className={`
        group relative bg-white rounded-lg overflow-hidden
        border border-gray-200 hover:border-blue-200
        hover:shadow-lg transition-all duration-300
        ${className}
      `}
    >
      <Link href={postUrl} className="block">
        {post.featuredImage && (
          <div
            className={`
              relative overflow-hidden
              ${isFeatured ? 'h-72 md:h-80' : isCompact ? 'h-36' : 'h-48 md:h-56'}
            `}
          >
            <Image
              src={post.featuredImage.url}
              alt={post.featuredImage.alt || post.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes={isFeatured ? '(max-width: 768px) 100vw, 800px' : '(max-width: 768px) 100vw, 400px'}
            />
            {showCategory && post.category && (
              <div className="absolute top-3 left-3">
                <span className="px-2.5 py-1 text-xs font-semibold text-white bg-blue-600 rounded">
                  {post.category.name}
                </span>
              </div>
            )}
          </div>
        )}

        <div className={`p-5 ${isFeatured ? 'p-6' : isCompact ? 'p-4' : ''}`}>
          {!post.featuredImage && showCategory && post.category && (
            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2 block">
              {post.category.name}
            </span>
          )}

          <h3
            className={`
              font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors
              ${isFeatured ? 'text-2xl' : isCompact ? 'text-base' : 'text-lg'}
            `}
          >
            {post.title}
          </h3>

          {showExcerpt && post.excerpt && !isCompact && (
            <p className={`text-gray-600 mb-3 line-clamp-2 ${isFeatured ? 'text-base' : 'text-sm'}`}>
              {post.excerpt}
            </p>
          )}

          {showTags && post.tags && post.tags.length > 0 && !isCompact && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          <div className={`flex flex-wrap items-center gap-2 text-gray-500 ${isFeatured ? 'text-sm' : 'text-xs'}`}>
            {showAuthor && (
              <div className="flex items-center gap-2">
                {post.author.avatar && (
                  <div className={`relative rounded-full overflow-hidden ${isFeatured ? 'w-7 h-7' : 'w-5 h-5'}`}>
                    <Image src={post.author.avatar} alt={post.author.name} fill className="object-cover object-top" />
                  </div>
                )}
                <span className="font-medium text-gray-700">{post.author.name}</span>
              </div>
            )}
            {showAuthor && showDate && <span>·</span>}
            {showDate && post.publishedAt && (
              <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
            )}
            {showReadingTime && post.readingTime && (
              <>
                <span>·</span>
                <span>{formatReadingTime(post.readingTime)}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}
